// src/apis/axiosInstance.js
import axios from "axios";
import { shareTokens } from "../Utils/tabSession";

const protocol = window.location.protocol;
const hostname = window.location.hostname;

// Local development only (REACT_APP_DEV_PROXY=true in .env.local, with `npm start`): the services are reached
// through the dev server (src/setupProxy.js), so the browser never meets their self-signed certificates.
const viaDevProxy = process.env.REACT_APP_DEV_PROXY === "true";
const service = (port, name) => (viaDevProxy ? `${window.location.origin}/__svc/${name}` : `${protocol}//${hostname}:${port}/feed`);

export const serviceUrls = {
  myFPO: service(8006, "fpo"),
  // Real mybusiness backend (repo-root mybusiness/, port 8007). Confirmed live 2026-07-10 —
  // curl https://localhost:8007/feed/company returns a structured 401 matching JwtFilter's
  // response shape, same signature fpo's 8006 gives for the same kind of request.
  myBusiness: service(8007, "mybusiness"),
  // No MyProducts backend exists yet — placeholder so createApi(...) doesn't throw
  // at module load (apis/MyProducts/myProductsApi.js).
  myProducts: service(8006, "fpo"),
};

export const authBaseUrl = service(8005, "feed");

const refreshTokenApi = async (refreshToken) => {
  try {
    const response = await axios.post(`${authBaseUrl}/refresh/token`, {
      token: refreshToken
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const createApiWithInterceptor = (baseURL, isAuthApi = false) => {
  const api = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
  });

  api.interceptors.request.use(
    (config) => {
      const token = sessionStorage.getItem("accessToken");
      if (token && !config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  if (isAuthApi) {
    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          
          if (originalRequest.url?.includes('/login') || 
              originalRequest.url?.includes('/refresh/token')) {
            return Promise.reject(error);
          }
          
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
              })
              .catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const refreshToken = sessionStorage.getItem("refreshToken");
            if (!refreshToken) {
              throw new Error("No refresh token available");
            }

            const response = await refreshTokenApi(refreshToken);
            const newAccessToken = response?.data?.token;
            const newRefreshToken = response?.data?.refreshToken;

            if (newAccessToken) {
              sessionStorage.setItem("accessToken", newAccessToken);
              if (newRefreshToken) {
                sessionStorage.setItem("refreshToken", newRefreshToken);
              }
              shareTokens(newAccessToken, newRefreshToken);   // the other open tabs use the new tokens too

              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              
              processQueue(null, newAccessToken);
              
              return api(originalRequest);
            } else {
              throw new Error("Failed to refresh token");
            }
          } catch (refreshError) {
            sessionStorage.removeItem("accessToken");
            sessionStorage.removeItem("refreshToken");
            sessionStorage.removeItem("userData");
            
            if (window.location.pathname !== "/") {
              window.location.href = "/";
            }
            
            processQueue(refreshError, null);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  return api;
};

export const createApi = (serviceKey) => {
  const baseURL = serviceUrls[serviceKey];
  if (!baseURL) throw new Error(`Base URL not defined for service: ${serviceKey}`);

  return createApiWithInterceptor(baseURL, false);
};

export const authApi = createApiWithInterceptor(authBaseUrl, true);